import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatRelativeDate } from '@/lib/utils'
import { Plus, Search, X, Building2, Globe, Phone, Users, Trash2, ArrowUpDown, Check, MapPin, GitMerge } from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'
import MergeModal from '@/components/MergeModal'

const INDUSTRIES = ['Technology', 'Healthcare', 'Finance', 'Real Estate', 'Construction', 'Retail', 'Manufacturing', 'Education', 'Legal', 'Marketing', 'Consulting', 'Home Services', 'Other']
const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+']
const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'created_at_asc', label: 'Oldest first' },
  { value: 'name_asc', label: 'Name A→Z' },
  { value: 'name_desc', label: 'Name Z→A' },
]

const STATUS_FILTERS = ['all', 'active', 'inactive', 'on-hold']
const STATUS_BADGE: Record<string, { color: string; bg: string; label: string }> = {
  active:    { color: 'var(--positive)',      bg: 'var(--positive-dim)',  label: 'Active' },
  inactive:  { color: 'var(--ink-secondary)', bg: 'var(--border-subtle)', label: 'Inactive' },
  'on-hold': { color: 'var(--warning)',       bg: 'var(--warning-dim)',   label: 'On hold' },
}

export function NewCompanyModal({ onClose, onCreated, initialName }: { onClose: () => void; onCreated: (id: string, name: string) => void; initialName?: string }) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({ name: initialName || '', domain: '', industry: '', size: '', phone: '', website: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Company name is required'); return }
    setSaving(true)
    const { data, error } = await supabase.from('companies').insert({
      ...form,
      workspace_id: workspace!.id,
    }).select().single()
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Company created')
    onCreated(data.id, data.name)
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 500, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New company</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corp" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Domain</label>
              <input value={form.domain} onChange={e => set('domain', e.target.value)} placeholder="acme.com" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 (555) 000-0000" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Industry</label>
              <select value={form.industry} onChange={e => set('industry', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select...</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Size</label>
              <select value={form.size} onChange={e => set('size', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select...</option>
                {SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Website</label>
            <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://acme.com" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes..." rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Creating...' : 'Create company'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function CompanyCard({ company, contactCount, locationCount, onClick, onDelete }: any) {
  const statusBadge = STATUS_BADGE[company.status || 'active'] || STATUS_BADGE.active
  return (
    <motion.div whileHover={{ y: -2 }} onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px', cursor: 'pointer', transition: 'border-color 200ms ease' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-border)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={company.name} size={40} variant="company" />
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 3px', letterSpacing: '-0.01em' }}>{company.name}</h3>
            {company.domain && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Globe style={{ width: 10, height: 10, color: 'var(--ink-tertiary)' }} />
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{company.domain}</span>
              </div>
            )}
          </div>
        </div>
        <button onClick={e => { e.stopPropagation(); onDelete() }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6, transition: 'all 150ms ease' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) var(--status-tint-dim), transparent)' }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)'; e.currentTarget.style.background = 'none' }}>
          <Trash2 style={{ width: 13, height: 13 }} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: statusBadge.color, background: statusBadge.bg, padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>
            {statusBadge.label}
          </span>
          {company.industry && (
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-dim)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>
              {company.industry}
            </span>
          )}
          {(company.tags || []).slice(0, 2).map((t: string) => (
            <span key={t} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>{t}</span>
          ))}
          {(company.tags || []).length > 2 && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', alignSelf: 'center' }}>+{company.tags.length - 2}</span>}
        </div>
        {company.phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Phone style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
            <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{company.phone}</span>
          </div>
        )}
        {company.size && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Users style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
            <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{company.size} employees</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ display: 'flex' }}>
            {[...Array(Math.min(contactCount, 3))].map((_, i) => (
              <div key={i} style={{ width: 20, height: 20, borderRadius: '50%', background: `hsl(${i * 60 + 200}, 70%, 60%)`, border: '2px solid var(--surface)', marginLeft: i > 0 ? -6 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: 'var(--accent-ink)' }}>{/* allow-hex: decorative placeholder hue rotation for the "this company has N contacts" stacked-avatar count indicator. Not real contact avatars; one-off chrome. */}
                {String.fromCharCode(65 + i)}
              </div>
            ))}
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
            {contactCount === 0 ? 'No contacts' : `${contactCount} contact${contactCount !== 1 ? 's' : ''}`}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {locationCount > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--positive)' }}>
              <MapPin style={{ width: 10, height: 10 }} /> {locationCount}
            </span>
          )}
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(company.updated_at)}</span>
        </div>
      </div>
    </motion.div>
  )
}

export default function Companies() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [industryFilter, setIndustryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [sort, setSort] = useState('created_at_desc')
  const [showNewModal, setShowNewModal] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showMergeModal, setShowMergeModal] = useState(false)
  const toggleSelect = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [view, setView] = useState<'grid' | 'list'>('grid')

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['companies', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('companies').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'company-rollup', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, company_id, company_name').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: locations = [] } = useQuery({
    queryKey: ['locations-for-companies', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('locations').select('id, company_id').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('companies').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['companies'] }); toast.success('Company deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  const getContactCount = (company: any) => {
    return contacts.filter((c: any) => c.company_id === company.id || c.company_name === company.name).length
  }

  const getLocationCount = (company: any) => {
    return locations.filter((l: any) => l.company_id === company.id).length
  }

  const industries = useMemo(() => {
    const set = new Set(companies.map((c: any) => c.industry).filter(Boolean))
    return Array.from(set) as string[]
  }, [companies])

  const filtered = useMemo(() => {
    let list = [...companies]
    if (industryFilter !== 'all') list = list.filter((c: any) => c.industry === industryFilter)
    if (statusFilter !== 'all') list = list.filter((c: any) => (c.status || 'active') === statusFilter)
    if (tagFilter !== 'all') list = list.filter((c: any) => (c.tags || []).includes(tagFilter))
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((c: any) => c.name?.toLowerCase().includes(q) || c.domain?.toLowerCase().includes(q) || c.industry?.toLowerCase().includes(q))
    }
    const sortMap: Record<string, [string, string]> = {
      'created_at_desc': ['created_at', 'desc'],
      'created_at_asc': ['created_at', 'asc'],
      'name_asc': ['name', 'asc'],
      'name_desc': ['name', 'desc'],
    }
    const [field, dir] = sortMap[sort] || ['created_at', 'desc']
    list.sort((a: any, b: any) => {
      const cmp = (a[field] || '') < (b[field] || '') ? -1 : (a[field] || '') > (b[field] || '') ? 1 : 0
      return dir === 'desc' ? -cmp : cmp
    })
    return list
  }, [companies, search, industryFilter, statusFilter, tagFilter, sort])

  // Distinct tags across the loaded companies → the tag-filter dropdown.
  const allTags = useMemo(() => Array.from(new Set((companies as any[]).flatMap((c: any) => c.tags || []))).sort(), [companies])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Companies</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {companies.length} total · {contacts.length} contacts linked
            </p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus style={{ width: 15, height: 15 }} /> New company
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s === 'all' ? 'All' : s.replace('-', ' ')}
              </button>
            ))}
          </div>

          {industries.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIndustryFilter('all')}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: industryFilter === 'all' ? 'var(--accent)' : 'var(--hover-bg)', color: industryFilter === 'all' ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: industryFilter === 'all' ? 'none' : '1px solid var(--border-subtle)' }}>
                All
              </button>
              {industries.slice(0, 4).map(ind => (
                <button key={ind} onClick={() => setIndustryFilter(ind)}
                  style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: industryFilter === ind ? 'var(--accent)' : 'var(--hover-bg)', color: industryFilter === ind ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: industryFilter === ind ? 'none' : '1px solid var(--border-subtle)' }}>
                  {ind}
                </button>
              ))}
            </div>
          )}

          {/* Tag filter — only when tags exist; composes (ANDs) with the others. */}
          {allTags.length > 0 && (
            <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}
              style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', outline: 'none', cursor: 'pointer' }}>
              <option value="all">Any tag</option>
              {(allTags as string[]).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
            {/* View toggle */}
            <div style={{ display: 'flex', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
              {(['grid', 'list'] as const).map(v => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '6px 12px', background: view === v ? 'var(--accent)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: view === v ? 'var(--accent-ink)' : 'var(--ink-tertiary)', transition: 'all 150ms ease' }}>
                  {v === 'grid' ? '⊞' : '☰'}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowSortMenu(!showSortMenu)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', cursor: 'pointer' }}>
                <ArrowUpDown style={{ width: 12, height: 12 }} />
                {SORT_OPTIONS.find(o => o.value === sort)?.label}
              </button>
              <AnimatePresence>
                {showSortMenu && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                    style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 180 }}>
                    {SORT_OPTIONS.map(opt => (
                      <button key={opt.value} onClick={() => { setSort(opt.value); setShowSortMenu(false) }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: sort === opt.value ? 'var(--accent-text)' : 'var(--ink-secondary)', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        {opt.label}
                        {sort === opt.value && <Check style={{ width: 12, height: 12 }} />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        {/* Bulk action bar (selection via the list view) — Merge at exactly 2. */}
        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-secondary)' }}>{selected.size} selected</span>
            {selected.size === 2 && (
              <button onClick={() => setShowMergeModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <GitMerge style={{ width: 13, height: 13 }} /> Merge
              </button>
            )}
            <button onClick={() => setSelected(new Set())}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 12, fontWeight: 600 }}>Clear</button>
          </div>
        )}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <Building2 style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
              {search || industryFilter !== 'all' || statusFilter !== 'all' ? 'No companies match your filters' : 'No companies yet'}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
              {search || industryFilter !== 'all' || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first company to organize your contacts'}
            </p>
            {!search && industryFilter === 'all' && statusFilter === 'all' && (
              <button onClick={() => setShowNewModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                <Plus style={{ width: 14, height: 14 }} /> New company
              </button>
            )}
          </div>
        ) : view === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {filtered.map((company: any) => (
              <CompanyCard
                key={company.id}
                company={company}
                contactCount={getContactCount(company)}
                locationCount={getLocationCount(company)}
                onClick={() => navigate(`/app/companies/${company.id}`)}
                onDelete={() => { if (window.confirm(`Delete ${company.name}?`)) deleteMutation.mutate(company.id) }}
              />
            ))}
          </div>
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
            {/* Column header — desktop only. Cards on mobile are self-labeling. */}
            {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 140px 120px 90px 90px 80px 40px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
              {['Company', 'Status', 'Industry', 'Size', 'Contacts', 'Locations', 'Updated', ''].map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
              ))}
            </div>
            )}
            {filtered.map((company: any, i: number) => {
              const sb = STATUS_BADGE[company.status || 'active'] || STATUS_BADGE.active
              const isLast = i === filtered.length - 1
              const contactCount = getContactCount(company)
              const locationCount = getLocationCount(company)

              // Mobile card layout. Industry + size inline as one secondary
              // line; contacts/locations counts as a second line. Delete
              // affordance kept in the header row (top-right, alongside
              // status badge). Same onClick → navigate as desktop.
              if (isMobile) {
                return (
                  <motion.div key={company.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    onClick={() => navigate(`/app/companies/${company.id}`)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar name={company.name} size={36} variant="company" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</p>
                        {company.domain && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.domain}</p>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: sb.color, background: sb.bg, padding: '2px 8px', borderRadius: 100 }}>{sb.label}</span>
                        <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete ${company.name}?`)) deleteMutation.mutate(company.id) }}
                          aria-label={`Delete ${company.name}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}>
                          <Trash2 style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {(company.industry || company.size) && (
                        <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                          {[company.industry, company.size ? `${company.size} employees` : null].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>
                        {contactCount} {contactCount === 1 ? 'contact' : 'contacts'} · {locationCount} {locationCount === 1 ? 'location' : 'locations'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>
                        Updated {formatRelativeDate(company.updated_at)}
                      </span>
                    </div>
                  </motion.div>
                )
              }

              // Desktop grid row — unchanged.
              return (
              <motion.div key={company.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                onClick={() => navigate(`/app/companies/${company.id}`)}
                style={{ display: 'grid', gridTemplateColumns: '1fr 100px 140px 120px 90px 90px 80px 40px', padding: '12px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', alignItems: 'center', cursor: 'pointer', transition: 'background 150ms ease' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div onClick={e => { e.stopPropagation(); toggleSelect(company.id) }} style={{ display: 'flex', flexShrink: 0 }}>
                    <input type="checkbox" checked={selected.has(company.id)} onChange={() => {}} style={{ width: 14, height: 14, cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  </div>
                  <Avatar name={company.name} size={30} variant="company" />
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{company.name}</p>
                    {company.domain && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{company.domain}</p>}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: sb.color, background: sb.bg, padding: '2px 8px', borderRadius: 100, width: 'fit-content' }}>{sb.label}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{company.industry || '—'}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{company.size ? `${company.size} emp.` : '—'}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{contactCount}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{locationCount}</span>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(company.updated_at)}</span>
                <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete ${company.name}?`)) deleteMutation.mutate(company.id) }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                  <Trash2 style={{ width: 13, height: 13 }} />
                </button>
              </motion.div>
              )
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewCompanyModal
            onClose={() => setShowNewModal(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['companies'] })}
          />
        )}
        {showMergeModal && selected.size === 2 && (() => {
          const [aId, bId] = Array.from(selected)
          const recordA = companies.find((c: any) => c.id === aId)
          const recordB = companies.find((c: any) => c.id === bId)
          if (!recordA || !recordB) return null
          return (
            <MergeModal entityType="company" recordA={recordA} recordB={recordB}
              onClose={() => setShowMergeModal(false)}
              onMerged={() => { queryClient.invalidateQueries({ queryKey: ['companies'] }); setSelected(new Set()); setShowMergeModal(false) }} />
          )
        })()}
      </AnimatePresence>
    </div>
  )
}